<template>
  <div class="relative h-full w-full">
    <div
      class="absolute inset-0 w-full h-full rounded-lg bg-saudi-green blur-[10px]"
    ></div>
    <div
      class="relative z-10 h-full md:min-h-[20rem] w-full rounded-xl border-2 border-saudi-green bg-saudi-dark"
    >
      <div
        :class="locale === 'ar' ? 'flex-row-reverse' : 'flex-row'"
        class="flex h-[15%] justify-evenly p-[3vw] md:p-[1vw] md:pt-[2vh] md:pb-[1vh] gap-2"
      >
        <div
          :class="locale === 'ar' ? 'flex-row-reverse' : 'flex-row'"
          class="flex h-full w-[50%] md:w-[67%] items-center rounded-lg border-2 border-saudi-purple/50 p-2"
        >
          <img
            class="ml-[1vw] w-[3vw] md:w-[1vw] md:ml-[0.5vw]"
            src="../assets/icons/search.png"
            alt="Search Icon"
          />
          <input
            type="text"
            :placeholder="
              locale === 'ar' ? '...' + $t('search') : $t('search') + '...'
            "
            v-model="term"
            class="w-full md:text-[1.2vw] text-[3vw] rounded-lg border-none bg-saudi-dark px-2 text-white focus:outline-none"
            :style="{ textAlign: locale === 'ar' ? 'right' : 'left' }"
          />
        </div>
        <div
          class="bg-saudi-dark w-[50%] md:w-[31%] h-full border-saudi-purple/50 border-2 rounded-lg text-white p-2"
        >
          <dropdown-menu withDropdownCloser>
            <template #trigger>
              <div
                class="h-full text-zinc-400 md:text-[1.2vw] text-[3vw] flex w-full justify-between cursor-pointer items-center"
              >
                <h1>{{ cardPosition }}</h1>
                <img
                  src="../assets/icons/arrowThick.svg"
                  class="hover-effect p-2"
                />
              </div>
            </template>

            <template #body>
              <ul
                :class="locale === 'ar' ? 'left-[1.35vw]' : 'right-[1.35vw]'"
                class="absolute top-[9vh] w-[50%] md:w-[30%] z-[100] flex p-2 flex-wrap justify-center gap-2 rounded-lg bg-[#0d090f] border-2 border-saudi-purple/50 font-bold"
              >
                <li
                  class="relative z-[130] h-[5vh] flex md:h-[4vh] w-[40%] md:w-[30%] cursor-pointer"
                  v-for="position in currentPositionList"
                  :key="position"
                  @click="selectPosition(position)"
                >
                  <a
                    dropdown-closer
                    class="flex w-full text-[1.46vw] md:text-[0.6vw] text-white z-[100] cursor-pointer items-center justify-center rounded-lg border-2 border-saudi-dark p-1 transition-colors hover:border-[#cf70cf]"
                    :class="[
                      cardPosition === position
                        ? 'bg-saudi-purple'
                        : 'bg-saudi-dark',
                      locale === 'ar' ? 'text-[1.46vw]' : 'text-[3vw]',
                    ]"
                  >
                    {{ position }}
                  </a>
                </li>
              </ul>
            </template>
          </dropdown-menu>
        </div>
      </div>

      <div
        :class="locale === 'ar' ? 'md:flex-row-reverse' : 'md:flex-row'"
        class="flex h-[50vh] w-full flex-col items-center md:flex-row"
      >
        <div class="h-full w-full overflow-y-auto md:w-[68%]">
          <div
            class="ml-[5%] mt-2 gap-y-[2vh] flex flex-wrap items-center justify-start md:ml-[3%] md:items-start md:justify-normal md:gap-2"
          >
            <PlayerSearchCard
              v-for="player in filteredPlayers"
              :key="player.Name"
              :player="player"
              @click="handlePlayerClick(player)"
              :matchingPlayerId="matchingPlayerId"
              :locale="locale"
            />
            <div
              v-if="!filteredPlayers.length"
              class="flex h-full w-full items-center justify-center text-center text-[5vw] md:text-[2vw] text-slate-300"
            >
              <div v-if="locale === 'ar'">.لم يتم العثور على لاعبين</div>
              <div v-else>No players found.</div>
            </div>
          </div>
        </div>
        <component
          :is="isMobile ? PlayerSummaryMobile : PlayerSummary"
          :player="selectedPlayer"
          @addPlayerToTeam="addPlayerToTeam"
          @addCaptain="addCaptain"
          :playerData="playerDataLower"
          :cardList="cardList"
          :captainId="captainId"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, computed, defineEmits, onMounted, onBeforeUnmount,
} from "vue";
import { useI18n } from "vue-i18n";
import PlayerSummary from "./PlayerSummary.vue";
import PlayerSummaryMobile from "./PlayerSummaryMobile.vue";
import PlayerSearchCard from "./PlayerSearchCard.vue";
import loadImages from "@/utils/loadImages";
import DropdownMenu from "v-dropdown-menu";

const props = defineProps({
  cardPosition: String,
  cardIndex: Number,
  cardList: Object,
  captainId: Number,
});

const playerImages = loadImages();
const { locale } = useI18n();
const isMobile = ref(window.innerWidth < 768);
const cardPosition = ref(props.cardPosition);
const positions = require("../data/positions.json");
const positionListAr = positions.positionListAr;
const positionListEn = positions.positionListEn;
const matchingPlayer = ref(null);
const matchingPlayerId = ref(0);

const playerDataSource =
  locale.value === "ar"
    ? require("../data/playerDataAr.json")
    : require("../data/playerDataEn.json");

const playerDataLower = playerDataSource.map((player) => {
  const playerId = `player${player.id}`;
  return {
    ...player,
    Name: player.Name.toLowerCase(),
    Given_name: player.Given_name.toLowerCase(),
    Family_name: player.Family_name.toLowerCase(),
    image:
      playerImages[playerId] ||
      "https://www.fifplay.com/img/fc/24/players/209331.webp",
  };
});


const term = ref("");
const selectedPlayer = ref(null);
const emit = defineEmits([
  "updateSelectedPlayer",
  "addPlayerToTeam",
  "addCaptain",
]);

const currentPositionList = computed(() =>
  locale.value === "ar" ? positionListAr : positionListEn
);

const selectPosition = (position) => {
  cardPosition.value = position;
  emit("updateSelectedPlayer", position); // Adjust if you have a specific event for position
};

const addPlayerToTeam = (player) => {
  emit("addPlayerToTeam", player);
};

const addCaptain = (playerId) => {
  emit("addCaptain", playerId);
};

const updateWindowWidth = () => {
  isMobile.value = window.innerWidth < 768;
};

onMounted(() => {
  window.addEventListener("resize", updateWindowWidth);
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", updateWindowWidth);
});

watch(
  () => props.cardPosition,
  (newPosition) => {
    cardPosition.value = newPosition;
  }
);

const oldPlayer = ref(0);
const handlePlayerClick = (player) => {
  if (player.id === oldPlayer.value) {
    selectedPlayer.value = null;
    oldPlayer.value = 0;
    matchingPlayerId.value = 0;
  } else {
    oldPlayer.value = player.id;
    selectedPlayer.value = player;
    emit("updateSelectedPlayer", selectedPlayer.value);
    matchingPlayerId.value = player.id;
  }
};

const filteredPlayers = computed(() => {
  const selectedPlayerId = props.cardList[props.cardIndex]?.playerId;
  const selectedPlayerIdList = props.cardList
    .filter((player) => player.playerId)
    .map((player) => player.playerId);

  return playerDataLower.filter((player) => {
    const playerId = player.id;
    const givenName = player.Given_name;
    const familyName = player.Family_name;
    const searchTermLower = term.value.toLowerCase();

    const matchesName =
      givenName.startsWith(searchTermLower) ||
      familyName.startsWith(searchTermLower);
    const matchesPosition = player.Position.split("/").includes(
      cardPosition.value
    );
    const isAlreadySelected = selectedPlayerIdList.includes(playerId);
    const canSelectPlayer = !isAlreadySelected || playerId === selectedPlayerId;

    return matchesName && matchesPosition && canSelectPlayer;
  });
});

watch(
  () => props.cardList[props.cardIndex],
  (newValue) => {
    const selectedPlayerId = newValue?.playerId;
    matchingPlayer.value =
      playerDataLower.find((player) => player.id === selectedPlayerId) || null;
    selectedPlayer.value = matchingPlayer.value;
    matchingPlayerId.value = Number(selectedPlayerId);
  }
);
</script>

<style>
.v-dropdown-menu__trigger {
  height: 100%;
}
.v-dropdown-menu.v-dropdown-menu--mode-click.v-dropdown-menu--direction-left {
  height: 100%;
}
</style>
